import { useCallback, useSyncExternalStore } from 'react';
import { PORTFOLIO_SCOPES, canTogglePortfolioScope, type PortfolioScope } from '@promanage/core';
import { useSession, type Session } from '../auth/session';

/**
 * Which portfolio the console is being read against — mine, or the whole agency's.
 *
 * ONE SELECTION FOR THE WHOLE APP. The properties register, the people register, the map, the
 * tenancy list and the portfolio tree are five views of one book, and a scope that reset per
 * route would leave them disagreeing about what "12 properties" counts. It is deliberately not
 * router state for the same reason the rail's collapse is not: a route change must not clear it.
 *
 * NOT A SECURITY CONTROL, and it could be deleted without weakening one. The server clamps every
 * scoped query against the membership grant on arrival (`portfolioProcedure` in apps/api), so a
 * stored value of `agency` in the browser of somebody who may not see the agency is answered with
 * their own portfolio, not with everyone's. What this decides is which of the two a permitted
 * reader gets, and what the control says it is showing.
 *
 * Kept per browser rather than per account: it is a working preference, like the rail's width,
 * and it belongs to the machine somebody is sitting at.
 */
export const STORAGE_KEY = 'promanage:portfolio-scope';

/**
 * `own` when nothing is stored.
 *
 * The narrow default is the product decision — a property manager's console opens on their own
 * houses. It costs a permitted reader one click to widen it, and that click is remembered; the
 * other way round, somebody granted the whole agency spends every morning wondering why the
 * register is showing them nine hundred properties.
 */
const DEFAULT_SCOPE: PortfolioScope = 'own';

function isScope(value: string | null): value is PortfolioScope {
  return value !== null && (PORTFOLIO_SCOPES as readonly string[]).includes(value);
}

function readStored(): PortfolioScope {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isScope(stored) ? stored : DEFAULT_SCOPE;
  } catch {
    // Private browsing or storage disabled — not an error, just no stored preference.
    return DEFAULT_SCOPE;
  }
}

// ── Store ────────────────────────────────────────────────────────────────────
// The same tiny external-store shape as lib/railCollapse.ts and lib/theme.ts, for the same
// reason: read from several places, changed rarely, and must survive a route change.

let selected: PortfolioScope = DEFAULT_SCOPE;
let initialised = false;
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

function init() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;
  selected = readStored();

  // Two tabs open on the same console should agree about what they are showing. Disagreeing
  // about a colour theme is a papercut; disagreeing about whose portfolio a total covers is a
  // figure read out of the wrong window.
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    selected = isScope(event.newValue) ? event.newValue : DEFAULT_SCOPE;
    emit();
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

if (typeof window !== 'undefined') init();

export function setPortfolioScope(next: PortfolioScope): void {
  selected = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // The preference will not persist; this session still works.
  }
  emit();
}

export interface PortfolioScopeState {
  /**
   * What to send with a scoped query, and what the screen should say it is showing.
   *
   * Already folded against the permission: somebody who may only see their own portfolio reads
   * `own` here whatever is stored, so a label built from this cannot promise a view the server
   * is about to narrow.
   */
  scope: PortfolioScope;
  setScope: (next: PortfolioScope) => void;
  /**
   * Whether to draw the control at all.
   *
   * `canTogglePortfolioScope` in @promanage/core, from `auth.me` — the server resolves the roles
   * and the membership grant together and sends the answer, so this file does not reproduce the
   * rule. Undefined session (the query has not landed yet) reads as false: drawing a toggle and
   * then removing it is worse than drawing it a moment late.
   */
  canToggle: boolean;
}

/**
 * The effective scope for code that runs outside React, such as a route preloader.
 *
 * This is the same decision `usePortfolioScope` exposes to components. Keeping it here means a
 * hover preload and the page it warms always use the same query key; otherwise a user restricted
 * to their own portfolio could preload the stored agency key and then fetch `own` again on paint.
 */
export function portfolioScopeFor(
  session: Session | null | undefined,
  requested: PortfolioScope = selected,
): PortfolioScope {
  const permitted = session?.portfolioScope ?? 'own';
  const canToggle =
    session !== null && session !== undefined && canTogglePortfolioScope(session.roles, permitted);
  return canToggle ? requested : 'own';
}

export function usePortfolioScope(): PortfolioScopeState {
  const session = useSession();

  const stored = useSyncExternalStore(
    subscribe,
    () => selected,
    () => DEFAULT_SCOPE, // server/prerender snapshot
  );

  const setScope = useCallback((next: PortfolioScope) => setPortfolioScope(next), []);

  // `auth.me.portfolioScope` is the WIDEST this session may ask for. Anything else stored is a
  // preference that no longer applies — a grant withdrawn this morning, most likely — and the
  // honest thing is to show what they will actually get rather than the setting they once chose.
  const permitted = session?.portfolioScope ?? 'own';
  const canToggle = session !== undefined && canTogglePortfolioScope(session.roles, permitted);

  return { scope: portfolioScopeFor(session, stored), setScope, canToggle };
}
